import * as angular from 'angular';

export interface CustomSelectDropdownOptions {
  value: string | number;
  label: string;
  class?: string; // if present, adds the class to the option and an empty element for custom styling
  example?: string; // if present, extra context copy added right aligned
  disabled?: boolean; // if option is enabled
}

/**
 * Custom (non-native) select input.
 *
 * This component builds a custom select input that leveragees uib dropdowns.
 * This can be used when this type of presentation is needed, but be mindful
 * about when this is replacing native browser select elements, and if it
 * actually makes sense to do so.
 */
class CustomSelectDropdownController implements angular.IController {
  private ngModelCtrl: angular.INgModelController;

  // bindings
  inputId: string;
  inputLabel: string;
  selectOptions: CustomSelectDropdownOptions[];
  isSmall: boolean;
  ngModel: string | number;

  /**
   * Get option data for selected item.
   */
  get selectedOption() {
    return this.selectOptions.find((option) => option.value === this.ngModel);
  }

  /**
   * Generate classes for option items.
   *
   * Add default and active classes. If present, add the custom
   * class. This can be used to target an additional element added
   * to the dropdown option to add an icon or color swatch,
   * or some other custom theming.
   */
  generateOptionClasses(value: string | number): string[] {
    const classes: string[] = [];
    const option = this.selectOptions.find((opt) => opt.value === value);
    classes.push(`option-${option.value}`);
    if (option.class) {
      classes.push('is-custom');
      classes.push(option.class);
    }
    if (option.value === this.selectedOption.value) {
      classes.push('active');
    }

    return classes;
  }

  /**
   * Set model.
   *
   * This will trigger `ng-change` on the component wrapper, if present.
   */
  setNgModel(value: string | number): void {
    this.ngModelCtrl.$setViewValue(value);
  }

  /**
   * Handle the select changing events and set model.
   */
  selectChanged(value: string | number): void {
    this.setNgModel(value);
  }
}

export const CustomSelectDropdownComponent: angular.IComponentOptions = {
  bindings: {
    inputId: '@',
    inputLabel: '@',
    selectOptions: '<',
    description: '<',
    isSmall: '<',
    ngModel: '<',
  },
  controller: CustomSelectDropdownController,
  controllerAs: 'ctrl',
  require: {
    ngModelCtrl: 'ngModel',
  },
  template: `
  <div class="custom-select-dropdown-field" ng-class="'custom-select-dropdown-' + ctrl.inputId">
    <label ng-if="ctrl.inputLabel" class="field-label">{{ ctrl.inputLabel }}</label>
    <div uib-dropdown class="custom-select-dropdown">
      <capra-button
        uib-dropdown-toggle
        variant="secondary"
        color="neutral"
        aria-label="{{ctrl.inputLabel}} dropdown"
        title="{{ctrl.inputLabel}} dropdown"
        text="{{ ctrl.selectedOption.label }}"
        size="small"
        icon="icon-caret-down"
        icon-location="right"
        type="button"
      />

      <ul uib-dropdown-menu>
        <li ng-repeat="selectOption in ctrl.selectOptions">
          <span ng-if="selectOption.disabled === true"
            ng-click="$event.stopPropagation()"
            class="disabled-option"
            ng-class="['option-' + selectOption.value]">
            {{ selectOption.label }}
            <span ng-if="selectOption.example" class="example-copy">{{ selectOption.example }}</span>
            <span ng-if="selectOption.class" class="custom-element"></span>
          </span>
          <button ng-if="!selectOption.hasOwnProperty('disabled') || selectOption.disabled === false"
            ng-click="ctrl.selectChanged(selectOption.value)"
            ng-class="ctrl.generateOptionClasses(selectOption.value)">
            {{ selectOption.label }}
            <span ng-if="selectOption.example" class="example-copy">{{ selectOption.example }}</span>
            <span ng-if="selectOption.class" class="custom-element"></span>
          </button>
        </li>
      </ul>
    </div>
    <p class="field-description" ng-if="ctrl.description">{{ ctrl.description }}</p>
  </div>
  `,
};
